import React from 'react';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeOutlined from '@mui/icons-material/AccessTimeOutlined';
import PersonOutline from '@mui/icons-material/PersonOutline';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import LayersOutlined from '@mui/icons-material/LayersOutlined';
import InventoryOutlined from '@mui/icons-material/InventoryOutlined';
import ChevronRight from '@mui/icons-material/ChevronRight';
import EventRepeatOutlined from '@mui/icons-material/EventRepeatOutlined';
import BuildOutlined from '@mui/icons-material/BuildOutlined';
import { Card, Badge, statusVariant } from '../../../components/ui';

/**
 * Type styling. Scheduled work reads navy, reactive work reads logo blue, and
 * each carries its own icon and full-word label — so the two are told apart by
 * shape and wording, not colour alone. Neither hue is a status colour, which
 * keeps the green/amber/red badge unambiguous.
 */
const TYPES = {
  preventive: {
    label: 'Preventive maintenance',
    icon: EventRepeatOutlined,
    rail: 'border-l-4 border-l-primary',
    chip: 'bg-primary/10 text-primary',
  },
  corrective: {
    label: 'Service request',
    icon: BuildOutlined,
    rail: 'border-l-4 border-l-accent',
    chip: 'bg-accent-soft text-accent',
  },
};

const fmtDate = (value) => (value ? new Date(value).toLocaleDateString('en-GB') : null);

/** One metadata row. Returns null when there's no value, so gaps don't appear. */
const Meta = ({ icon: Icon, label, value }) =>
  value ? (
    <div className="flex items-center gap-2 text-sm text-muted-fg">
      <Icon style={{ fontSize: 16 }} className="shrink-0" aria-hidden="true" />
      <span className="truncate">
        <span className="text-muted-fg">{label}: </span>
        <span className="text-foreground">{value}</span>
      </span>
    </div>
  ) : null;

/**
 * Work-order summary card.
 *
 * The whole card is the target rather than a 120px-tall "View" button pinned to
 * the right edge — that button was the only 44px-compliant element in the old
 * app and it dominated the card on desktop while cramping it on mobile.
 */
const QDFWorkOrderCard = ({
  entry,
  onView,
  userRole,
  isSelectionMode = false,
  isSelected = false,
  onSelect,
}) => {
  const completedBy =
    entry?.inspection?.completedByUser?.trim() || entry?.inspection?.preparedBy?.trim();
  const completedAt = entry?.inspection?.completedAt;
  const isPPM = entry.maintenanceType === 'preventive';
  const type = isPPM ? TYPES.preventive : TYPES.corrective;
  const TypeIcon = type.icon;

  return (
    <Card
      selected={isSelected}
      interactive={!isSelectionMode}
      onClick={isSelectionMode ? undefined : () => onView(entry)}
      className={`group mb-4 ${type.rail}`}
    >
      <div className="flex items-start gap-3 p-4 sm:p-5">
        {isSelectionMode && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelect(entry._id)}
            aria-label={`Select work order ${entry.scNumber}`}
            className="mt-1 h-5 w-5 shrink-0 cursor-pointer rounded border-border-strong"
          />
        )}

        <span
          className={`hidden h-10 w-10 shrink-0 items-center justify-center rounded-lg sm:flex ${type.chip}`}
          aria-hidden="true"
        >
          <TypeIcon style={{ fontSize: 20 }} />
        </span>

        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-foreground">
              <TypeIcon style={{ fontSize: 15 }} className="sm:hidden" aria-hidden="true" />
              {type.label}
            </span>
            <span className="text-xs text-muted-fg">·&nbsp;{isPPM ? entry.ppmType : 'Corrective'}</span>
            <Badge variant={statusVariant(entry.scStatus)} size="sm" dot className="ml-auto">
              {entry.scStatus || 'Unknown'}
            </Badge>
          </div>

          <h3 className="tabular text-base font-semibold text-foreground sm:text-lg">
            {entry.scNumber}
          </h3>
          {entry.scDescription && (
            <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-muted-fg">
              {entry.scDescription}
            </p>
          )}

          <div className="mt-4 grid gap-x-6 gap-y-2 sm:grid-cols-2 xl:grid-cols-3">
            <Meta
              icon={CalendarTodayOutlined}
              label="Created"
              value={fmtDate(entry.creationDate || entry.createdAt)}
            />
            <Meta
              icon={AccessTimeOutlined}
              label="Target"
              value={fmtDate(entry.targetResolution)}
            />
            <Meta icon={PlaceOutlined} label="Location" value={entry.location} />
            {isPPM && entry.ppmType === 'Monthly' && (
              <Meta icon={LayersOutlined} label="Zone" value={entry.zone} />
            )}
            <Meta icon={InventoryOutlined} label="Asset" value={entry.assetTag} />
            <Meta icon={PersonOutline} label="Completed by" value={completedBy} />
            <Meta icon={CalendarTodayOutlined} label="Completed" value={fmtDate(completedAt)} />
          </div>
        </div>

        {!isSelectionMode && (
          <ChevronRight
            style={{ fontSize: 20 }}
            aria-hidden="true"
            className="mt-1 shrink-0 self-center text-muted-fg transition-transform duration-base group-hover:translate-x-1 group-hover:text-accent"
          />
        )}
      </div>
    </Card>
  );
};

export default QDFWorkOrderCard;
